import { appConfig } from '../../../config/app-config';
import type { CustomerGroup } from '../../home/types/home.types';
import type { ProductWithAddOns } from '../types/product.types';

export interface PassPriceInfo {
  displayPrice: string;
  originalPrice: string | null;
  discountName: string | null;
  savedAmount: string | null;
}

export const formatPrice = (price?: { value?: { centAmount?: number; currencyCode?: string } }) => {
  if (!price || !price.value || !price.value.currencyCode) return 'N/A';
  const amount = (price.value.centAmount || 0) / 100;
  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: price.value.currencyCode,
  }).format(amount);
};

export const getPassPriceInfo = (
  product: ProductWithAddOns,
  selectedRateId?: string | null,
  customerGroups?: CustomerGroup[]
): PassPriceInfo => {
  const locale = appConfig.defaults.locale || 'en';

  // 1. Resolve Master Variant
  type MasterVariant = NonNullable<ProductWithAddOns['masterVariant']> & {
    prices?: Array<{
      customerGroup?: { id: string; obj?: { id: string; key?: string } };
      value?: { centAmount?: number; currencyCode?: string };
      discounted?: {
        value?: { centAmount?: number; currencyCode?: string };
        discount?: { obj?: { name: Record<string, string> } };
      };
    }>;
  };

  const masterVariant = (product?.masterVariant ||
    (
      product as unknown as {
        masterData?: { current?: { masterVariant?: MasterVariant } };
      }
    )?.masterData?.current?.masterVariant) as MasterVariant | undefined;

  // 2. Resolve target Price Object
  const hasCustomerGroupPrice = masterVariant?.prices?.some((p) => p.customerGroup);
  let priceObj: MasterVariant['prices'] extends (infer P)[] | undefined ? P : never =
    undefined as never;

  if (hasCustomerGroupPrice && selectedRateId) {
    priceObj = masterVariant?.prices?.find(
      (p) => p.customerGroup && p.customerGroup.id === selectedRateId
    ) as typeof priceObj;

    if (!priceObj && customerGroups) {
      const selectedGroup = customerGroups.find((g) => g.id === selectedRateId);
      if (selectedGroup?.key) {
        priceObj = masterVariant?.prices?.find(
          (p) => p.customerGroup?.obj?.key === selectedGroup.key
        ) as typeof priceObj;
      }
    }
  }

  if (!priceObj) {
    priceObj = masterVariant?.prices?.[0] as typeof priceObj;
  }

  // 3. Format primary display prices
  let displayPrice = 'N/A';
  let originalPrice: string | null = null;

  if (priceObj) {
    if ((priceObj as { discounted?: unknown }).discounted) {
      displayPrice = formatPrice(
        (priceObj as { discounted: { value?: { centAmount?: number; currencyCode?: string } } })
          .discounted
      );
      originalPrice = formatPrice(
        priceObj as { value?: { centAmount?: number; currencyCode?: string } }
      );
    } else {
      displayPrice = formatPrice(
        priceObj as { value?: { centAmount?: number; currencyCode?: string } }
      );
    }
  }

  // 4. Extract specific discounts/savings
  let discountName: string | null = null;
  let savedAmount: string | null = null;

  type PriceWithDiscount = {
    discounted?: {
      discount?: { obj?: { name?: Record<string, string> } };
      value?: { centAmount?: number };
    };
    value?: { centAmount?: number; currencyCode?: string };
  };

  const priceWithDiscount = priceObj as PriceWithDiscount | undefined;
  if (priceWithDiscount?.discounted?.discount?.obj) {
    const discountObj = priceWithDiscount.discounted.discount.obj;
    discountName = (discountObj.name?.[locale] || discountObj.name?.['en']) ?? null;

    const originalAmount = priceWithDiscount.value?.centAmount || 0;
    const discountedAmount = priceWithDiscount.discounted.value?.centAmount || 0;
    const savedCents = originalAmount - discountedAmount;

    if (savedCents > 0) {
      savedAmount = new Intl.NumberFormat('en', {
        style: 'currency',
        currency: priceWithDiscount.value?.currencyCode || 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(savedCents / 100);
      savedAmount = `-${savedAmount}`;
    }
  }

  return { displayPrice, originalPrice, discountName, savedAmount };
};
